/**
 * OrbitPrint — shared shell pieces (approved prototype, 2026-09-16).
 *
 * AuthShell: the sign-in / register / reset pages. Left: the thesis — a big
 * condensed headline with the gradient kit line, the lead, and the hero scene
 * (route + relay + network). Right: the form on a kit-edged card with the
 * fleet lamps beneath it. Stacks to one column on phones.
 */
import { Link } from 'react-router-dom';
import { useOrbitPath } from '../OrbitAuthContext';
import HeroScene from './HeroScene';
import '../orbit.css';

export function OrbitMark({ small = false }) {
  return <span className={`orbit-mark${small ? ' is-small' : ''}`} aria-hidden="true" />;
}

export function OrbitWordmark({ small = false }) {
  const p = useOrbitPath();
  return (
    <Link to={p('/')} className="inline-flex items-center gap-3 no-underline" data-testid="orbit-brand-link" style={{ color: 'var(--ob-ink)' }}>
      <OrbitMark small={small} />
      <span className={`orbit-display ${small ? 'text-lg' : 'text-xl'}`} style={{ letterSpacing: '.03em' }}>OrbitPrint</span>
    </Link>
  );
}

export function Lamp({ state = 'ready', children, testid, title }) {
  return (
    <span className={`orbit-lamp is-${state}`} data-testid={testid} title={title}><i />{children}</span>
  );
}

function FleetLamps() {
  return (
    <div className="orbit-panel p-4 flex flex-col gap-3" aria-hidden="true">
      <div className="flex justify-between">
        <span className="orbit-label">What you'll see</span>
        <span className="orbit-label orbit-mono" style={{ letterSpacing: 0 }}>live status</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Lamp state="ready">Ready</Lamp>
        <Lamp state="busy">Printing</Lamp>
        <Lamp state="error">Attn</Lamp>
        <Lamp state="offline">Offline</Lamp>
      </div>
    </div>
  );
}

export function AuthShell({ title, lead, children, footer }) {
  return (
    <div className="orbit-root min-h-screen">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-6 sm:py-8">
        <header className="orbit-boot orbit-d1 flex items-center justify-between pb-5" style={{ borderBottom: '1px solid var(--ob-rule)' }}>
          <OrbitWordmark />
        </header>

        <div className="mt-8 sm:mt-10 grid gap-10 lg:grid-cols-[1.1fr_.9fr] lg:items-start">
          <div className="hero">
            <h1 className="orbit-display orbit-boot orbit-d2" style={{ fontSize: 'clamp(52px, 7vw, 92px)', margin: 0 }}>
              Print from<br />anywhere.<br /><span className="orbit-grad-text">To your own<br />printers.</span>
            </h1>
            <p className="orbit-boot orbit-d3 orbit-ink2 text-[17px] leading-relaxed max-w-lg mt-6">
              Install OrbitPrint HUB on the computer your printers are plugged into. Every printer on it appears here, with a live status you can trust — read from the printer itself, never guessed.
            </p>
            <div className="orbit-boot orbit-d4 mt-7 hidden md:block"><HeroScene /></div>
          </div>

          <div className="orbit-card is-kit orbit-boot orbit-d3 p-7 sm:p-9 flex flex-col gap-5">
            <h2 className="orbit-display" style={{ fontSize: 36, margin: 0 }}>{title}</h2>
            {lead && <p className="orbit-ink2 text-[15px] leading-relaxed -mt-1">{lead}</p>}
            <div>{children}</div>
            {footer && <div className="flex justify-between gap-4 text-sm orbit-muted">{footer}</div>}
            <FleetLamps />
          </div>
        </div>
      </div>
    </div>
  );
}

export function OrbitInput({ label, testid, hint, className = '', ...props }) {
  const id = props.id || `ob-${(props.name || label || 'field').toString().toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <div className="orbit-field mb-4">
      <label className="orbit-label" htmlFor={id}>{label}</label>
      <input id={id} className={`orbit-input ${className}`.trim()} data-testid={testid} {...props} />
      {hint && <p className="text-xs orbit-muted">{hint}</p>}
    </div>
  );
}

export function OrbitButton({ children, variant = 'primary', busy = false, testid, className = '', size, block, ...props }) {
  const cls = ['orbit-btn', `is-${variant}`, size === 'small' ? 'is-small' : '', block ? 'is-block' : '', className].filter(Boolean).join(' ');
  return (
    <button className={cls} data-testid={testid} disabled={busy || props.disabled} {...props}>
      {busy ? 'One moment…' : children}
    </button>
  );
}

export function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
  );
}

export function PrinterIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6V3h8v3M3 6h10v5h-2v2H5v-2H3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
  );
}
